import { OpenInNew } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Link, Tooltip } from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridPinnedColumns,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import moment from 'moment/moment';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { resolveNextTaskSeq } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/data';
import { WorklistTaskFormData } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/data';

export type UseAddWorklistTasksTableProps = {
  employeeId: number;
  employeeTaskRows: WorklistTaskFormData[];
  handleTasksUpdate: (updatedTasks: WorklistTaskFormData[]) => void;
  newTaskData?: WorklistTaskFormData;
};

export const useAddWorklistTasksTable = (
  props: UseAddWorklistTasksTableProps,
) => {
  const { employeeId, employeeTaskRows, handleTasksUpdate, newTaskData } =
    props;

  const { t } = useTranslation();
  const { getActiveEmployeeById } = useAllEmployees();

  const [selectedTask, setSelectedTask] = useState<
    WorklistTaskFormData | undefined
  >(undefined);

  const onAddNewTask = useCallback(() => {
    if (!!newTaskData) {
      const nextTask: Pick<WorklistTaskFormData, 'taskId' | 'seq'> = {
        taskId: employeeTaskRows.length + 100,
        seq: resolveNextTaskSeq(employeeTaskRows),
      };

      setSelectedTask({
        ...newTaskData,
        ...nextTask,
        worklistId: 'placeholderWorklistId',
      });
    }
  }, [newTaskData, employeeTaskRows]);

  const handleDeleteTask = (row: WorklistTaskFormData) => {
    handleTasksUpdate(employeeTaskRows.filter((t) => t.taskId !== row.taskId));
  };

  const onTableRowUpdate = (taskData: WorklistTaskFormData): Promise<void> => {
    const isTaskMatch = (a: WorklistTaskFormData, b: WorklistTaskFormData) =>
      a.taskId === b.taskId;
    const isExistingTask = employeeTaskRows.some((task) =>
      isTaskMatch(task, taskData),
    );
    const updatedTasks = isExistingTask
      ? employeeTaskRows.map((task) => {
          return isTaskMatch(task, taskData) ? taskData : task;
        })
      : employeeTaskRows.concat(taskData);

    handleTasksUpdate(updatedTasks);
    return Promise.resolve();
  };

  const getSelectedEmployeeInfo = useCallback(
    (employeeId: number) => {
      const employee = getActiveEmployeeById(employeeId);

      return !!employee
        ? `${employee.fEmpFirstName} ${employee.fEmpLastName}, ${employee.fEmpPersonNumber}`
        : '-';
    },
    [getActiveEmployeeById],
  );

  const columns: GridColDef<WorklistTaskFormData>[] = [
    {
      field: 'title',
      headerName: t('label_collaboration_task_subject'),
      flex: 1.2,
    },
    {
      field: 'description',
      headerName: t('label_collaboration_task_description'),
      flex: 1.8,
      align: 'left',
      headerAlign: 'left',
      cellClassName: 'multiline-description',
    },
    {
      field: 'link',
      headerName: t('link'),
      minWidth: 60,
      flex: 0.1,
      renderCell: ({ value }) => {
        return !!value ? (
          <Tooltip title={value} placement={'right'} arrow={true}>
            <Link href={value} target={'_blank'}>
              <OpenInNew />
            </Link>
          </Tooltip>
        ) : (
          <></>
        );
      },
    },
    {
      field: 'dueDate',
      headerName: t('label_collaboration_task_due_on'),
      minWidth: 120,
      valueFormatter: ({ value }) => {
        return !!value ? moment(value).format('L') : '';
      },
    },
    {
      field: 'responsibleEmployeeId',
      headerName: t('label_collaboration_task_employee_responsible'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const { value } = params;

        return !!value ? getSelectedEmployeeInfo(value) : '';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions'),
      minWidth: 60,
      cellClassName: 'actions',
      getActions: ({ row }) => {
        const { templateId } = row;

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={t('edit')}
            className="textPrimary"
            onClick={() => setSelectedTask(row)}
            color="inherit"
          />,
          ...(!templateId
            ? [
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label={t('delete')}
                  onClick={() => handleDeleteTask(row)}
                  color="inherit"
                />,
              ]
            : []),
        ];
      },
    },
  ];

  const pinnedColumns: GridPinnedColumns = {
    right: ['actions'],
  };

  return {
    dataGridProps: {
      columns,
      rows: employeeTaskRows,
      pinnedColumns,
    },
    editTaskProps: {
      open: !!selectedTask,
      selectedTask,
      employeeId,
      onClose: () => setSelectedTask(undefined),
      onProcessSubmission: onTableRowUpdate,
    },
    customTasks: {
      enabled: !!newTaskData,
      onAddNewTask,
    },
  };
};
